import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Target, Crosshair, Zap, CircleDot, Loader2, ArrowRight, TrendingUp, Minus, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { TREND_STYLES } from "@/lib/stateMeta";

const WEAPON_ICON = { Archery: Target, Rifle: Crosshair, Muzzleloader: Zap, "Any weapon": CircleDot, "Any legal weapon": CircleDot };
const ODDS_OPTS = [{ v: null, l: "Any" }, { v: 25, l: "25%+" }, { v: 50, l: "50%+" }, { v: 75, l: "75%+" }];

const Toggle = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`px-3 py-1.5 text-xs font-medium border transition-colors ${active ? "bg-forest text-bone border-forest" : "bg-white text-charcoal/70 border-saddle/25 hover:border-forest"}`}>
    {children}
  </button>
);

function PersonalOdds({ p }) {
  if (!p) return null;
  const meets = p.eligibility === "meets";
  const random = p.eligibility === "random";
  const color = random ? "text-[#3B5B8C]" : meets ? "text-[#2E7D46]" : "text-[#B7761F]";
  return (
    <div className="mt-3 bg-forest/5 border border-forest/10 px-3 py-2" data-testid="personal-odds">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wide text-charcoal/50">Your estimated odds</span>
        <span className={`font-serif text-2xl leading-none tabular-nums ${color}`}>{p.est_odds_pct}%</span>
      </div>
      <p className={`text-[11px] mt-1 ${color}`}>{p.label}{!meets && !random && p.years_to_typical ? ` · ~${p.years_to_typical} yr${p.years_to_typical === 1 ? "" : "s"} to build` : ""}</p>
    </div>
  );
}

function HuntCard({ h, onOpen }) {
  const t = TREND_STYLES[h.trend?.key] || TREND_STYLES.unknown;
  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.28 }}
      className="bg-white border border-saddle/20 p-5 flex flex-col" data-testid={`hunt-card-${h.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-serif text-xl text-forest leading-tight">{h.unit}</p>
          <p className="text-xs text-charcoal/60 mt-0.5">{h.species} — {h.weapon} · {h.residency}</p>
        </div>
        <div className="text-right">
          <p className="font-serif text-3xl text-blaze leading-none tabular-nums">{h.draw_odds_pct}%</p>
          <p className="text-[10px] uppercase tracking-wide text-charcoal/45 mt-1">Draw odds</p>
        </div>
      </div>
      <p className="text-xs text-charcoal/55 mt-3">{h.season}</p>
      <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-saddle/15 text-sm">
        <div><p className="text-[10px] uppercase tracking-wide text-charcoal/45">Points</p><p className="text-forest font-medium">{h.points_required}</p></div>
        <div><p className="text-[10px] uppercase tracking-wide text-charcoal/45">Point creep</p><p className="text-forest font-medium">{h.point_creep}</p></div>
      </div>
      <PersonalOdds p={h.personalized} />
      <div className="flex items-center gap-2 mt-4">
        <span className={`w-2 h-2 rounded-full ${t.dot}`} />
        <span className={`text-xs font-medium ${t.text}`}>{h.trend?.label}</span>
      </div>
      <button onClick={() => onOpen(h.id)} className="mt-4 w-full bg-forest text-bone py-2.5 text-xs font-semibold uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-blaze transition-colors" data-testid={`view-hunt-${h.id}`}>
        View Hunt <ArrowRight size={14} />
      </button>
    </motion.div>
  );
}

export function HuntResults({ state, species, weapons, points, setPoints, onOpen }) {
  const [weapon, setWeapon] = useState(null);
  const [residency, setResidency] = useState("Nonresident");
  const [minOdds, setMinOdds] = useState(null);
  const [hunts, setHunts] = useState(null);

  const load = useCallback(() => {
    setHunts(null);
    const params = { species };
    if (weapon) params.weapon = weapon;
    if (residency) params.residency = residency;
    if (minOdds != null) params.min_odds = minOdds;
    if (points != null) params.points = points;
    api.exploreHunts(state, params).then((d) => setHunts(d.hunts)).catch(() => setHunts([]));
  }, [state, species, weapon, residency, minOdds, points]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="hunt-results">
      {/* Weapon selector */}
      {weapons?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-5" data-testid="weapon-filters">
          <Toggle active={!weapon} onClick={() => setWeapon(null)} testid="weapon-all">All methods</Toggle>
          {weapons.map((w) => {
            const Icon = WEAPON_ICON[w] || CircleDot;
            return <Toggle key={w} active={weapon === w} onClick={() => setWeapon(w)} testid={`weapon-${w}`}><span className="inline-flex items-center gap-1.5"><Icon size={13} /> {w}</span></Toggle>;
          })}
        </div>
      )}

      {/* Toggle filters + Your Points */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mb-7 pb-5 border-b border-saddle/15">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wide text-charcoal/45 mr-1">Residency</span>
          <Toggle active={residency === "Resident"} onClick={() => setResidency("Resident")} testid="res-resident">Resident</Toggle>
          <Toggle active={residency === "Nonresident"} onClick={() => setResidency("Nonresident")} testid="res-nonresident">Nonresident</Toggle>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wide text-charcoal/45 mr-1">Min odds</span>
          {ODDS_OPTS.map((o) => <Toggle key={o.l} active={minOdds === o.v} onClick={() => setMinOdds(o.v)} testid={`odds-${o.l}`}>{o.l}</Toggle>)}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-[10px] uppercase tracking-wide text-charcoal/45 mr-1">Your points</span>
          <button onClick={() => setPoints(Math.max(0, (points || 0) - 1))} className="w-8 h-8 grid place-items-center border border-saddle/25 bg-white text-forest hover:border-forest" aria-label="Decrease points" data-testid="points-minus"><Minus size={14} /></button>
          <span className="w-8 text-center font-serif text-xl text-forest tabular-nums" data-testid="points-value">{points ?? 0}</span>
          <button onClick={() => setPoints((points || 0) + 1)} className="w-8 h-8 grid place-items-center border border-saddle/25 bg-white text-forest hover:border-forest" aria-label="Increase points" data-testid="points-plus"><Plus size={14} /></button>
        </div>
      </div>

      {hunts === null ? (
        <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-forest" /></div>
      ) : hunts.length === 0 ? (
        <div className="py-16 text-center" data-testid="hunts-empty">
          <TrendingUp className="mx-auto text-saddle/40 mb-3" size={28} />
          <p className="text-charcoal/60">No hunts match these filters. Try widening residency or lowering the odds threshold.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {hunts.map((h) => <HuntCard key={h.id} h={h} onOpen={onOpen} />)}
        </div>
      )}
    </div>
  );
}
